import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FeatherBell } from '../ui/icons';
import { socialWallApi } from '../../services/api';
import { supabase } from '../../config/supabase';
import './NotificationBell.css';

const NotificationBell = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const { count } = await socialWallApi.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await socialWallApi.getNotifications(1, 20, false);
      console.log('Notifications API response:', response);
      const fetchedNotifications = response?.notifications || response || [];
      console.log('Fetched notifications:', fetchedNotifications);
      setNotifications(Array.isArray(fetchedNotifications) ? fetchedNotifications : []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load notifications when dropdown opens and calculate position
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
      // Calculate dropdown position
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const dropdownWidth = 360;
        const padding = 8;
        
        // Try to position to the right of the button
        let left = rect.right + padding;
        
        // If dropdown would go off-screen, position it to the left instead
        if (left + dropdownWidth > window.innerWidth) {
          left = rect.left - dropdownWidth - padding;
          // If still off-screen, align to right edge of viewport
          if (left < 0) {
            left = window.innerWidth - dropdownWidth - padding;
          }
        }
        
        setDropdownPosition({
          top: rect.bottom + padding,
          left: Math.max(padding, left)
        });
      }
    }
  }, [isOpen, fetchNotifications]);

  // Fetch unread count on mount and set up real-time subscription
  useEffect(() => {
    if (!user?.id) return;

    fetchUnreadCount();

    // Set up real-time subscription for new notifications
    const channel = supabase
      .channel('social_wall_notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'social_wall_notifications',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          // New notification received
          fetchUnreadCount();
          // If dropdown is open, refresh notifications
          if (isOpen) {
            fetchNotifications();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'social_wall_notifications',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          // Notification updated (e.g., marked as read)
          fetchUnreadCount();
          if (isOpen) {
            fetchNotifications();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, isOpen, fetchUnreadCount, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside the dropdown (rendered via portal)
      const dropdown = document.querySelector('.notification-dropdown');
      if (dropdown && dropdown.contains(event.target)) {
        return; // Don't close if clicking inside dropdown
      }
      
      // Check if click is on the bell button
      if (buttonRef.current && buttonRef.current.contains(event.target)) {
        return; // Don't close if clicking the bell button
      }
      
      // Close if clicking outside both dropdown and button
      setIsOpen(false);
    };

    if (isOpen) {
      // Use click instead of mousedown to allow onClick handlers to fire first
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside, true);
      }, 0);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isOpen]);

  const handleNotificationClick = async (notification) => {
    console.log('Notification clicked:', notification);
    
    if (!notification.is_read) {
      try {
        console.log('Marking notification as read:', notification.id);
        console.log('API call: PUT /api/social-wall/notifications/' + notification.id + '/read');
        
        const result = await socialWallApi.markAsRead(notification.id);
        console.log('Mark as read result:', result);
        
        // Update local state
        setNotifications(prev =>
          prev.map(n =>
            n.id === notification.id ? { ...n, is_read: true } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        
        // Refresh unread count to ensure sync
        await fetchUnreadCount();
      } catch (error) {
        console.error('Error marking notification as read:', error);
        console.error('Error details:', error.response?.data || error.message);
        alert('Failed to mark notification as read. Please try again.');
        return; // Don't navigate if there's an error
      }
    } else {
      console.log('Notification already read');
    }
    
    setIsOpen(false);
    navigate('/social-wall');
  };

  const handleMarkAllAsRead = async () => {
    try {
      console.log('Marking all notifications as read');
      const result = await socialWallApi.markAllAsRead();
      console.log('Mark all as read result:', result);
      
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
      alert('Failed to mark all notifications as read. Please try again.');
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getPostPreview = (content) => {
    if (!content) return 'New post';
    return content.length > 50 ? `${content.substring(0, 50)}...` : content;
  };

  const dropdownContent = isOpen ? (
    <div 
      className="notification-dropdown"
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`
      }}
    >
          <div className="notification-dropdown-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button
                className="mark-all-read-button"
                onClick={handleMarkAllAsRead}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="notification-loading">Loading...</div>
            ) : !notifications || notifications.length === 0 ? (
              <div className="notification-empty">No notifications</div>
            ) : (
              notifications.map((notification) => {
                if (!notification || !notification.id) {
                  console.warn('Invalid notification:', notification);
                  return null;
                }
                return (
                  <div
                    key={notification.id}
                    className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Notification item clicked, calling handleNotificationClick');
                      console.log('Notification ID:', notification.id);
                      console.log('Is read:', notification.is_read);
                      
                      // Call the handler
                      await handleNotificationClick(notification);
                    }}
                  >
                    <div className="notification-content">
                      <div className="notification-text">
                        <strong>
                          {notification.post?.user?.display_name || 'Someone'}
                        </strong>
                        {' '}posted on the wall
                      </div>
                      <div className="notification-preview">
                        {getPostPreview(notification.post?.content)}
                      </div>
                      <div className="notification-time">
                        {formatTime(notification.created_at)}
                      </div>
                    </div>
                    {!notification.is_read && (
                      <div className="notification-dot"></div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {(notifications && notifications.length > 0) && (
            <div className="notification-dropdown-footer">
              <button
                className="view-all-button"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/social-wall');
                }}
              >
                View all
              </button>
            </div>
          )}
        </div>
  ) : null;

  return (
    <>
      <div className="notification-bell-container" ref={dropdownRef}>
        <button
          ref={buttonRef}
          className="notification-bell-button"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Notifications"
        >
          <FeatherBell />
          {unreadCount > 0 && (
            <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </button>
      </div>
      {isOpen && createPortal(dropdownContent, document.body)}
    </>
  );
};

export default NotificationBell;

